import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RocketLaunchIcon, SparklesIcon, TrophyIcon } from '@heroicons/react/24/outline';
import { useLocation } from 'react-router-dom';

export default function DefaultLoading({ pages, message, context = 'general', transparent = false }) {
  const location = useLocation();
  const [currentMessage, setCurrentMessage] = useState('');
  const [messageIndex, setMessageIndex] = useState(0);

  // Context-aware loading messages
  const loadingMessages = {
    general: [
      "🎮 LOADING GAMES...",
      "⚡ PREPARING YOUR EXPERIENCE...",
      "🚀 ALMOST READY...",
      "✨ LOADING CONTENT..."
    ],
    authentication: [
      "🔐 VERIFYING CREDENTIALS...",
      "👤 SETTING UP YOUR PROFILE...",
      "🎯 LOADING GAME STUDIO...",
      "🚀 LAUNCHING DASHBOARD..."
    ],
    subscription: [
      "💳 CHECKING SUBSCRIPTION...",
      "🎮 VALIDATING ACCESS...",
      "⚡ PREPARING YOUR STUDIO...",
      "✨ FINALIZING SETUP..."
    ],
    campaigns: [
      "🎲 LOADING CAMPAIGNS...",
      "📊 FETCHING ANALYTICS...",
      "🎮 PREPARING GAMES...",
      "🚀 READY TO PLAY..."
    ]
  };

  // Determine context from URL if not provided
  const getContextFromPath = () => {
    const path = location.pathname;
    if (path.includes('/login') || path.includes('/register')) return 'authentication';
    if (path.includes('/plan') || path.includes('/subscription')) return 'subscription';
    if (path.includes('/campaign')) return 'campaigns';
    return 'general';
  };

  const currentContext = context === 'general' ? getContextFromPath() : context;
  const messages = message ? [message] : loadingMessages[currentContext] || loadingMessages.general;

  useEffect(() => {
    // Load Share Tech Mono font
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Share+Tech+Mono:wght@400&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    
    return () => {
      if (document.head.contains(link)) {
        document.head.removeChild(link);
      }
    };
  }, []);

  useEffect(() => {
    if (messages.length > 1) {
      const interval = setInterval(() => {
        setMessageIndex((prev) => (prev + 1) % messages.length);
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [messages.length]);

  useEffect(() => {
    setCurrentMessage(messages[messageIndex] || messages[0]);
  }, [messageIndex, messages]);

  const getIcon = () => {
    switch (currentContext) {
      case 'authentication':
        return RocketLaunchIcon;
      case 'subscription':
        return SparklesIcon;
      case 'campaigns':
        return TrophyIcon;
      default:
        return RocketLaunchIcon;
    }
  };

  const IconComponent = getIcon();

  return (
    <div 
      className={`flex flex-col items-center justify-center relative overflow-hidden ${transparent ? 'h-full' : 'min-h-screen'}`}
      style={{
        background: transparent ? 'transparent' : 'linear-gradient(135deg, #2C3E50 0%, #34495E 50%, #2C3E50 100%)',
        fontFamily: '"Share Tech Mono", monospace'
      }}
    >
      {/* Animated Background Elements */}
      <div className="absolute inset-0 opacity-10">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 rounded-full"
            style={{
              backgroundColor: '#F1C40F',
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              opacity: [0, 1, 0],
              scale: [0.5, 1, 0.5],
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 3,
            }}
          />
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="flex flex-col items-center justify-center space-y-8 relative z-10"
      >
        {/* Enhanced Spinner with Dynamic Icon */}
        <div className="relative">
          <motion.div 
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            className="w-20 h-20 border-4 rounded-full shadow-lg"
            style={{
              borderColor: '#34495E',
              borderTopColor: '#F1C40F',
              borderRightColor: '#E74C3C'
            }}
          />
          <motion.div 
            className="absolute inset-0 flex items-center justify-center"
            animate={{ 
              scale: [1, 1.1, 1],
              rotate: [0, 10, -10, 0] 
            }}
            transition={{ 
              duration: 2, 
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            <IconComponent 
              className="w-10 h-10"
              style={{ color: '#E74C3C' }}
            />
          </motion.div>
        </div>

        {/* Dynamic Loading Message */}
        <motion.div
          className="text-center min-h-[60px] flex items-center justify-center"
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={currentMessage}
              initial={{ opacity: 0, y: 20, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.9 }}
              transition={{ duration: 0.5, ease: "easeInOut" }}
              className="text-center"
            >
              <p 
                className="text-xl font-bold tracking-wider px-4"
                style={{ 
                  color: '#F1C40F',
                  textShadow: '2px 2px 0px #B7950B, 0 0 20px rgba(241, 196, 15, 0.3)'
                }}
              >
                {currentMessage}
              </p>
            </motion.div>
          </AnimatePresence>
        </motion.div>

        {/* Progress Indicator */}
        {messages.length > 1 && (
          <motion.div 
            className="flex space-x-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            {messages.map((_, index) => (
              <motion.div
                key={index}
                className="w-2 h-2 rounded-full"
                style={{
                  backgroundColor: index === messageIndex ? '#F1C40F' : '#34495E',
                }}
                animate={{
                  scale: index === messageIndex ? 1.2 : 1,
                }}
                transition={{ duration: 0.3 }}
              />
            ))}
          </motion.div>
        )}

        {/* Pulsing Subtitle */}
        <motion.div
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="text-sm font-bold"
          style={{ 
            color: '#BDC3C7'
          }}
        >
          {currentContext === 'authentication' && "Setting up your gaming profile..."}
          {currentContext === 'subscription' && "Configuring your game studio..."}
          {currentContext === 'campaigns' && "Loading your creative workspace..."}
          {currentContext === 'general' && "Preparing your experience..."}
        </motion.div>

        {/* Loading Bar Animation */}
        <motion.div 
          className="w-64 h-1 bg-gray-700 rounded-full overflow-hidden"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
        >
          <motion.div
            className="h-full rounded-full"
            style={{
              background: 'linear-gradient(90deg, #F1C40F, #E74C3C, #F1C40F)'
            }}
            animate={{
              x: ['-100%', '100%'],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
        </motion.div>
      </motion.div>
    </div>
  );
}